import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import { getWorkOrders } from '../api';
import { WorkOrder, WorkOrderStatus } from '../types/workOrder';
import { useNotification } from '../contexts/NotificationContext';
import { formatDate } from '../utils';

const WorkOrderList: React.FC = () => {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: 'createdAt',
    sortOrder: -1 as 1 | -1 | 0 | null | undefined,
  });
  const [filters, setFilters] = useState({
    global: '',
    status: '' as WorkOrderStatus | '',
  });
  const { addNotification } = useNotification();
  const dt = useRef<DataTable<WorkOrder[]>>(null);

  const fetchWorkOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getWorkOrders({
        page: lazyParams.page,
        limit: lazyParams.rows,
        status: filters.status,
        sortBy: lazyParams.sortField,
        sortOrder: lazyParams.sortOrder === 1 ? 'asc' : 'desc',
      });
      setWorkOrders(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      console.error('Failed to fetch work orders:', error);
      addNotification('error', 'Failed to fetch work orders');
    } finally {
      setLoading(false);
    }
  }, [lazyParams, filters.status, addNotification]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  const onPage = (event: DataTablePageEvent) => {
    setLazyParams(prevParams => ({
      ...prevParams,
      first: event.first,
      rows: event.rows,
      page: event.page ? event.page + 1 : 1,
    }));
  };

  const onSort = (event: DataTableSortEvent) => {
    setLazyParams(prevParams => ({
      ...prevParams,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prevFilters => ({ ...prevFilters, global: e.target.value }));
    setLazyParams(prevParams => ({ ...prevParams, first: 0, page: 1 }));
  };

  const onStatusFilterChange = (e: DropdownChangeEvent) => {
    setFilters(prevFilters => ({ ...prevFilters, status: e.value }));
    setLazyParams(prevParams => ({ ...prevParams, first: 0, page: 1 }));
  };

  const actionBodyTemplate = (rowData: WorkOrder) => {
    return (
      <div className="flex justify-content-center">
        <Link to={`/work-orders/${rowData.id}`}>
          <Button icon="pi pi-eye" className="p-button-rounded p-button-info mr-2" />
        </Link>
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">Manage Work Orders</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          type="search"
          onInput={onGlobalFilterChange}
          placeholder="Search..."
        />
      </span>
    </div>
  );

  const renderSkeleton = () => {
    return (
      <div className="grid">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="col-12 mb-3">
            <div className="flex justify-content-between align-items-center">
              <Skeleton width="60%" height="2rem" />
              <Skeleton width="20%" height="2rem" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={[
          { label: 'All', value: '' },
          { label: 'Pending', value: 'PENDING' },
          { label: 'In Progress', value: 'IN_PROGRESS' },
          { label: 'Completed', value: 'COMPLETED' },
        ]}
        onChange={onStatusFilterChange}
        placeholder="Select Status"
        className="p-column-filter"
      />
    );
  };

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          {loading ? (
            renderSkeleton()
          ) : (
            <DataTable
              ref={dt}
              value={workOrders}
              lazy
              paginator
              first={lazyParams.first}
              rows={lazyParams.rows}
              totalRecords={totalRecords}
              onPage={onPage}
              onSort={onSort}
              sortField={lazyParams.sortField}
              sortOrder={lazyParams.sortOrder}
              header={header}
              globalFilter={filters.global}
              emptyMessage="No work orders found."
              responsiveLayout="scroll"
            >
              <Column field="id" header="Work Order ID" sortable filter filterPlaceholder="Search by ID" />
              <Column field="purchaseOrder.poNumber" header="Purchase Order" filter filterPlaceholder="Search by PO" />
              <Column 
                field="status" 
                header="Status" 
                sortable 
                filter 
                filterElement={statusFilterTemplate}
              />
              <Column
                field="createdAt"
                header="Created At"
                sortable
                body={(rowData) => formatDate(rowData.createdAt)}
              />
              <Column body={actionBodyTemplate} headerStyle={{ width: '10%' }} bodyStyle={{ textAlign: 'center' }} />
            </DataTable>
          )}
          <div className="flex justify-content-end mt-3">
            <Link to="/create-work-order">
              <Button label="Add New Work Order" icon="pi pi-plus" className="p-button-success" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderList;